import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";

import { QUERY_SITE_NAMES, QUERY_USERS } from "../graphql/queries";
import PageUsers from "../components/pages/users/Users";
import { ErrorComponent } from "../components/pages/Error";
import { useLoading } from "../components/context/LoadingContext";
import { useConnection } from "../components/context/ConnectionContext";

// Server-side MAX_LIMIT; sorting and filtering are client-side, so the full
// user and site lists are fetched page by page.
const PAGE_SIZE = 100;

interface UsersData {
    users: CoreUser[];
    // site reference -> customer-facing name
    siteNames: Record<string, string>;
}

function useAllUsers() {
    const client = useApolloClient();
    const { setLoading } = useLoading();
    const { setConnectionLost } = useConnection();

    const [data, setData] = useState<UsersData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const dataRef = useRef(data);
    dataRef.current = data;

    const fetchAll = useCallback(async () => {
        // Keep stale rows on screen during a refetch; overlay only on first load.
        if (dataRef.current === null) {
            setLoading(true);
        }

        const fetchAllEdges = async <TNode,>(
            query: any,
            getConnection: (data: any) => {
                edges: { node: TNode }[];
                pageInfo: PageInfo;
            }
        ): Promise<TNode[]> => {
            const fetchPage = (offset: number) =>
                client.query({
                    query,
                    variables: { limit: PAGE_SIZE, offset },
                    fetchPolicy: "network-only",
                });

            const first = getConnection((await fetchPage(0)).data);
            const offsets = [];
            for (
                let offset = PAGE_SIZE;
                offset < first.pageInfo.totalCount;
                offset += PAGE_SIZE
            ) {
                offsets.push(offset);
            }
            const rest = await Promise.all(offsets.map(fetchPage));

            return [
                first.edges,
                ...rest.map((r) => getConnection(r.data).edges),
            ]
                .flat()
                .map((edge) => edge.node);
        };

        try {
            const [users, sites] = await Promise.all([
                fetchAllEdges<CoreUser>(QUERY_USERS, (d) => d.account.users),
                fetchAllEdges<{ name: string; reference: string }>(
                    QUERY_SITE_NAMES,
                    (d) => d.account.sites
                ),
            ]);

            const siteNames: Record<string, string> = {};
            sites.forEach((site) => {
                siteNames[site.reference] = site.name;
            });

            setData({ users, siteNames });
            setError(null);
            setConnectionLost(false);
        } catch (err: any) {
            if (err?.networkError?.message?.includes("401")) {
                window.location.reload();
                return;
            }
            try {
                window.ElasticAmp?.captureError(err);
            } catch (e) {
                console.error("AMP error", err);
            }
            setConnectionLost(!!err?.networkError);
            setError(err?.message ?? String(err));
        } finally {
            setLoading(false);
        }
    }, [client, setLoading, setConnectionLost]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    return { data, error, refetch: fetchAll };
}

const UsersRoute = () => {
    const { data, error, refetch } = useAllUsers();

    if (error && data === null) {
        return <ErrorComponent error={error} refetch={refetch} />;
    }

    if (data === null) return null;

    return (
        <PageUsers
            users={data.users}
            siteNames={data.siteNames}
            refetch={refetch}
        />
    );
};

export { UsersRoute };
